import {
  SearchTokenParams,
  SolType,
  TokenBalanceEntry,
  TokenInfoWithPrice,
  useTokensQuery,
} from "@/api";
import { useRouter } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { usePortfolio } from "./usePortfolio";

export function useSearchTokensQuery({
  skip = false,
  solType,
  persistSearchInUrl,
  addresses,
}: {
  skip?: boolean;
  solType: SolType;
  persistSearchInUrl?: boolean;
  addresses?: string[];
}) {
  const { navigate, parseLocation } = useRouter();
  const searchParams = parseLocation().search;
  const [search, setSearch] = useState(
    persistSearchInUrl &&
      "search" in searchParams &&
      typeof searchParams.search === "string"
      ? searchParams.search ?? ""
      : "",
  );
  const [debouncedParams, setDebouncedParams] = useState<SearchTokenParams>({
    filter: search,
    page: 1,
    solType,
    list_address: addresses,
  });

  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();
  const [rows, setRows] = useState<TokenInfoWithPrice[]>([]);
  const tokensQuery = useTokensQuery(debouncedParams, { skip });

  // If the last item in rows doesn't match the last item from tokensQuery or
  // the lengths are different, assume they are new results
  if (
    tokensQuery.data &&
    !tokensQuery.isFetching &&
    (rows[rows.length - 1]?.address !==
      tokensQuery.data.data[tokensQuery.data.data.length - 1]?.address ||
      (addresses?.length && tokensQuery.data.data.length !== rows.length))
  ) {
    // Do this here instead of a useEffect to prevent flashing an empty state
    // (i.e. having renders where isLoading is false but there are no rows since
    // the useEffect doesn't run immediately)
    setRows((prev) =>
      (tokensQuery.data?.currentPage ?? 1) === 1
        ? tokensQuery.data?.data ?? []
        : prev.concat(tokensQuery.data?.data ?? []),
    );
  }

  const updateSearchParams = useCallback(
    (newSearch: Record<string, string | number | boolean>) => {
      void navigate({
        search: {
          ...searchParams,
          ...newSearch,
        },
        replace: true,
      });
    },
    [navigate, searchParams],
  );

  const setPage = useCallback((mix: number | ((prev: number) => number)) => {
    setDebouncedParams((prev) => ({
      ...prev,
      page: typeof mix === "function" ? mix(prev.page ?? 1) : mix,
    }));
  }, []);

  const updateSearch = useCallback(
    (value: string) => {
      setSearch(value);
      if (persistSearchInUrl) {
        updateSearchParams({ search: value });
      }

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }

      timeoutIdRef.current = setTimeout(() => {
        setDebouncedParams((prev) => ({
          ...prev,
          filter: value,
          page: 1,
        }));
      }, 500);
    },
    [updateSearchParams, persistSearchInUrl],
  );

  const hasNextPage = typeof tokensQuery.data?.nextPage === "number";

  const goNextPage = useCallback(() => {
    if (hasNextPage) {
      setPage((debouncedParams.page ?? 1) + 1);
    }
  }, [hasNextPage, setPage, debouncedParams.page]);

  return {
    isLoading: tokensQuery.isLoading,
    isFetching: tokensQuery.isFetching,
    search,
    debouncedParams,
    rows,
    hasNextPage,
    setSearch: updateSearch,
    setPage,
    goNextPage,
  };
}

function filterPortfolioTokens(token: TokenBalanceEntry, search: string) {
  const searchLowercase = search.toLowerCase();
  const name = token.name?.toLowerCase();
  const symbol = token.symbol?.toLowerCase();
  return (
    (Boolean(token.address) &&
      token.address.toLowerCase().startsWith(searchLowercase)) ||
    (name &&
      (name.includes(searchLowercase) || searchLowercase.includes(name))) ||
    (symbol &&
      (symbol.includes(searchLowercase) || searchLowercase.includes(symbol)))
  );
}

export function useSearchTokensPortfolioQuery({
  skip = false,
  solType,
  persistSearchInUrl,
}: {
  skip?: boolean;
  solType: SolType;
  persistSearchInUrl?: boolean;
}) {
  const { navigate, parseLocation } = useRouter();
  const searchParams = parseLocation().search;
  const [search, setSearch] = useState(
    persistSearchInUrl &&
      "search" in searchParams &&
      typeof searchParams.search === "string"
      ? searchParams.search ?? ""
      : "",
  );
  const [debouncedParams, setDebouncedParams] = useState<SearchTokenParams>({
    filter: search,
    page: 1,
    solType,
  });
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();
  const portfolio = usePortfolio({ skip, solType });

  const rows =
    portfolio.tokens?.filter((token) => filterPortfolioTokens(token, search)) ??
    [];

  const updateSearchParams = useCallback(
    (newSearch: Record<string, string | number | boolean>) => {
      void navigate({
        search: {
          ...searchParams,
          ...newSearch,
        },
        replace: true,
      });
    },
    [navigate, searchParams],
  );

  const setPage = useCallback((mix: number | ((prev: number) => number)) => {
    setDebouncedParams((prev) => ({
      ...prev,
      page: typeof mix === "function" ? mix(prev.page ?? 1) : mix,
    }));
  }, []);

  const updateSearch = useCallback(
    (value: string) => {
      setSearch(value);
      if (persistSearchInUrl) {
        updateSearchParams({ search: value });
      }

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }

      timeoutIdRef.current = setTimeout(() => {
        setDebouncedParams({ filter: value, page: 1, solType });
      }, 500);
    },
    [updateSearchParams, solType, persistSearchInUrl],
  );

  const goNextPage = useCallback(() => {
    // noop
  }, []);

  return {
    isFetching: portfolio.isFetching,
    isLoading: portfolio.isLoading,
    isEmpty: portfolio.tokens?.length === 0,
    search,
    debouncedParams,
    rows,
    hasNextPage: false,
    setSearch: updateSearch,
    setPage,
    goNextPage,
  };
}
